import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  PROJECT_WORKFLOW_STATUSES,
  type ProjectWorkflowStatusKey,
} from "@/lib/projects";

/* ─────────────────────────── Typen ─────────────────────────── */

/** Einzelner Status-Chip inkl. „Alle“ (nur UI). */
export type StatusFilter = "alle" | StatusFilterValue;

/** Auswählbare Statuswerte (ohne „Alle“). Mehrfachauswahl möglich. */
export type StatusFilterValue = "abgeschlossen" | ProjectWorkflowStatusKey;

/**
 * Jahr-Filter:
 *  - `null` → "Alle Jahre"
 *  - `"S"`  → S-Ordner
 *  - sonst  → konkretes Jahr (z. B. 2024)
 */
export type YearFilter = number | "S" | null;

export interface ProjectFilters {
  q: string;
  jahr: YearFilter;
  kunde: string;
  ordner: string;
  sordner: string;
  /** Leer = alle Status. Sonst OR über die gewählten Status. */
  status: StatusFilterValue[];
}

const DEFAULTS: ProjectFilters = {
  q: "",
  jahr: new Date().getFullYear(),
  kunde: "",
  ordner: "",
  sordner: "",
  status: [],
};

const STATUS_VALUE_SET = new Set<string>(["abgeschlossen", ...PROJECT_WORKFLOW_STATUSES]);

/* ─────────────────────────── Helpers ─────────────────────────── */

function parseYear(raw: string | null): YearFilter {
  if (!raw) return DEFAULTS.jahr;
  const value = raw.trim();
  if (!value) return DEFAULTS.jahr;
  if (value.toLowerCase() === "all") return null;
  if (value.toUpperCase() === "S") return "S";
  const num = Number(value);
  return Number.isFinite(num) ? num : DEFAULTS.jahr;
}

/** Parst `?status=offen` oder `?status=offen,beauftragt` (kommagetrennt). */
function parseStatus(raw: string | null): StatusFilterValue[] {
  if (!raw) return [];
  const out: StatusFilterValue[] = [];
  const seen = new Set<string>();
  for (const part of raw.split(",")) {
    const value = part.trim().toLowerCase();
    if (!value || value === "alle" || !STATUS_VALUE_SET.has(value) || seen.has(value)) {
      continue;
    }
    seen.add(value);
    out.push(value as StatusFilterValue);
  }
  return out;
}

function serializeStatus(values: StatusFilterValue[]): string | null {
  if (!values.length) return null;
  return values.join(",");
}

function serializeYear(value: YearFilter): string | null {
  if (value === null) return "all";
  if (value === "S") return "S";
  return String(value);
}

/** True wenn mindestens ein Filter von den Standard‑URL‑Werten abweicht (Suche/Jahr/Kunde/Ordner/Status). */
export function projectHasActiveFilters(f: ProjectFilters): boolean {
  const defaultYear = new Date().getFullYear();
  return (
    !!f.q.trim() ||
    f.jahr !== defaultYear ||
    !!f.kunde ||
    !!f.ordner ||
    !!f.sordner ||
    f.status.length > 0
  );
}

/* ─────────────────────────── Hook ─────────────────────────── */

export function useProjectFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters: ProjectFilters = useMemo(() => {
    const qRaw = searchParams.get("q") ?? "";
    return {
      q: qRaw,
      jahr: parseYear(searchParams.get("jahr")),
      kunde: searchParams.get("kunde") ?? "",
      ordner: searchParams.get("ordner") ?? "",
      sordner: searchParams.get("sordner") ?? "",
      status: parseStatus(searchParams.get("status")),
    };
  }, [searchParams]);

  const update = useCallback(
    (patch: Partial<ProjectFilters>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          // Bei Jahresänderung kaskadierende Resets, damit es keine inkonsistenten
          // Kombinationen gibt (z. B. Jahr 2023 + Kunde aus 2024 + alter Ordner).
          if (patch.jahr !== undefined) {
            const ser = serializeYear(patch.jahr);
            if (ser === null) next.delete("jahr");
            else next.set("jahr", ser);
            // Kunde/Ordner/SOrdner zurücksetzen, wenn Jahr-Modus wechselt
            const wasS = filters.jahr === "S";
            const isS = patch.jahr === "S";
            const wasNull = filters.jahr === null;
            const isNull = patch.jahr === null;
            if (wasS !== isS || wasNull !== isNull || patch.jahr !== filters.jahr) {
              next.delete("kunde");
              next.delete("ordner");
              if (!isS) next.delete("sordner");
            }
          }
          if (patch.q !== undefined) {
            if (patch.q) next.set("q", patch.q);
            else next.delete("q");
          }
          if (patch.kunde !== undefined) {
            if (patch.kunde) next.set("kunde", patch.kunde);
            else next.delete("kunde");
            // Kunde wechselt → Ordner zurücksetzen
            next.delete("ordner");
          }
          if (patch.ordner !== undefined) {
            if (patch.ordner) next.set("ordner", patch.ordner);
            else next.delete("ordner");
          }
          if (patch.sordner !== undefined) {
            if (patch.sordner) next.set("sordner", patch.sordner);
            else next.delete("sordner");
          }
          if (patch.status !== undefined) {
            const ser = serializeStatus(patch.status);
            if (ser === null) next.delete("status");
            else next.set("status", ser);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams, filters.jahr],
  );

  const reset = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  return { filters, update, reset, defaults: DEFAULTS };
}
